import { useState, useEffect } from 'react';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Minus, Trash2, ChevronDown, ChevronRight } from 'lucide-react';
import { IngredientIcon } from '@/components/ui/ingredient-icon';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { useMenuItems } from '@/hooks/useMenuItems';
import { useMenuCategories } from '@/hooks/useMenuCategories';
import { useCreateOrder, useOrders, useDeleteOrderItem } from '@/hooks/useOrders';
import { useAllMenuItemSpecialties, type MenuItemSpecialty } from '@/hooks/useAllMenuItemSpecialties';
import { useAllMenuItemOptions, getMenuItemOptionsFromBatch } from '@/hooks/useAllMenuItemOptions';
import { useMenuItemAvailability } from '@/hooks/useMenuItemAvailability';
import { useToast } from '@/hooks/use-toast';
import { formatPrice } from '@/lib/utils';
import type { MenuItem, OrderItem as OrderItemEntity, MenuItemOption } from '@/types/entities';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

interface OrderItem {
  menuItem: MenuItem;
  quantity: number;
  notes: string;
  selectedOptionId: string | null;
  options?: MenuItemOption[];
}

// Component to display specialty badges for a menu item
// Now accepts pre-loaded specialties from batch query instead of fetching per-item
function MenuItemSpecialtiesBadges({
  specialties,
}: {
  specialties: MenuItemSpecialty[];
}) {
  const { i18n } = useTranslation();

  if (!specialties || specialties.length === 0) {
    return null;
  }

  return (
    <div className="flex flex-wrap gap-1 mt-1">
      {specialties.map((specialty) => (
        <Badge
          key={specialty.specialtyId}
          style={{ backgroundColor: specialty.color || '#888' }}
          className="text-white text-xs"
        >
          {i18n.language === 'ar' ? specialty.nameAr : specialty.nameFr}
        </Badge>
      ))}
    </div>
  );
}

interface OrderModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  tableId: string;
  tableNumber: number;
  restaurantId: string;
  staffId: string;
  allowNegativeStock?: boolean;
}

export function OrderModal({
  open,
  onOpenChange,
  tableId,
  tableNumber,
  restaurantId,
  staffId,
  allowNegativeStock = false,
}: OrderModalProps) {
  const { t, i18n } = useTranslation();
  const { toast } = useToast();
  const { data: menuItems = [], isLoading } = useMenuItems(restaurantId);
  const { data: categories = [] } = useMenuCategories(restaurantId);
  const { data: allOrders = [] } = useOrders(restaurantId);
  const { data: availabilityMap = {}, isLoading: isLoadingAvailability } =
    useMenuItemAvailability(restaurantId);
  // Batch load all specialties and options for the restaurant - eliminates N+1 queries
  const { data: allSpecialtiesMap } = useAllMenuItemSpecialties(restaurantId);
  const { data: allOptionsMap } = useAllMenuItemOptions(restaurantId);
  const createOrder = useCreateOrder(restaurantId);
  const deleteOrderItem = useDeleteOrderItem(restaurantId);

  const [selectedItems, setSelectedItems] = useState<OrderItem[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());
  const [hasInitializedCategories, setHasInitializedCategories] = useState(false);
  const [existingSubOrders, setExistingSubOrders] = useState<
    Array<{ id: string; items: OrderItemEntity[]; status: string }>
  >([]);
  // Mobile step state: 'menu' or 'review'
  const [mobileStep, setMobileStep] = useState<'menu' | 'review'>('menu');

  // Find all existing sub-orders for this table when modal opens
  useEffect(() => {
    if (open) {
      // Find parent order for this table
      const parentOrder = allOrders.find(
        (order) =>
          order.tableId === tableId &&
          !order.parentOrderId && // Parent order
          order.status !== 'completed' &&
          order.status !== 'cancelled' &&
          (order.status as string) !== 'checked'
      );

      // Get all sub-orders with items (exclude cancelled sub-orders)
      const subOrdersWithItems =
        parentOrder?.subOrders
          ?.filter(
            (subOrder) =>
              subOrder.items &&
              subOrder.items.length > 0 &&
              subOrder.status !== 'cancelled' // Don't show cancelled sub-orders
          )
          .map((subOrder) => ({
            id: subOrder.id,
            items: subOrder.items || [],
            status: subOrder.status,
          })) || [];

      setExistingSubOrders(subOrdersWithItems);
      // Reset selected items when opening modal
      setSelectedItems([]);
      // Reset mobile step to menu
      setMobileStep('menu');
      // Reset category initialization to expand all categories
      setHasInitializedCategories(false);
    }
  }, [open, tableId, allOrders]);

  const filteredMenuItems = menuItems.filter((item) => {
    const name = i18n.language === 'ar' ? item.nameAr : item.nameFr;
    // Filter by search query and admin availability flag only
    // Show all items including those without stock (they will be shown as disabled)
    return item.isAvailable && name.toLowerCase().includes(searchQuery.toLowerCase());
  });

  // Group menu items by category
  const groupedMenuItems = filteredMenuItems.reduce(
    (acc, item) => {
      const categoryId = item.categoryId || 'uncategorized';
      if (!acc[categoryId]) {
        acc[categoryId] = [];
      }
      acc[categoryId].push(item);
      return acc;
    },
    {} as Record<string, MenuItem[]>
  );

  // Get category name
  const getCategoryName = (categoryId: string | null) => {
    if (!categoryId) return t('menu.noCategory');
    const category = categories.find((c) => c.id === categoryId);
    return category
      ? i18n.language === 'ar'
        ? category.nameAr
        : category.nameFr
      : t('menu.noCategory');
  };

  // Toggle category expansion
  const toggleCategory = (categoryId: string) => {
    setExpandedCategories((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(categoryId)) {
        newSet.delete(categoryId);
      } else {
        newSet.add(categoryId);
      }
      return newSet;
    });
  };

  // Keep all categories folded by default - no auto-expansion
  React.useEffect(() => {
    if (Object.keys(groupedMenuItems).length > 0 && !hasInitializedCategories) {
      // Start with empty set - all categories folded
      setExpandedCategories(new Set());
      setHasInitializedCategories(true);
    }
  }, [groupedMenuItems, hasInitializedCategories]);

  const addItem = (menuItem: MenuItem) => {
    const availability = availabilityMap[menuItem.id];
    const availableQuantity = allowNegativeStock ? Infinity : (availability?.availableQuantity ?? 999999);

    const existingItem = selectedItems.find((item) => item.menuItem.id === menuItem.id);
    if (existingItem) {
      // Check if adding one more would exceed available stock
      if (!allowNegativeStock && existingItem.quantity >= availableQuantity) {
        toast({
          title: t('order.insufficientStock'),
          description: t('order.onlyXAvailable', { count: availability?.availableQuantity ?? 0 }),
          variant: 'destructive',
        });
        return;
      }
      setSelectedItems(
        selectedItems.map((item) =>
          item.menuItem.id === menuItem.id ? { ...item, quantity: item.quantity + 1 } : item
        )
      );
    } else {
      // Check if item is available
      if (!allowNegativeStock && availability?.availableQuantity === 0) {
        toast({
          title: t('order.outOfStock'),
          description: t('order.itemNotAvailable'),
          variant: 'destructive',
        });
        return;
      }
      // Get options from batch-loaded data - no additional query needed!
      const options = getMenuItemOptionsFromBatch(allOptionsMap, menuItem.id);

      // Determine initial selected option:
      // 1. If options exist and there's a default, use it
      // 2. If options are required and there's only one option, auto-select it
      // 3. Otherwise null (no option)
      let initialOptionId: string | null = null;
      const defaultOption = options.find((opt) => opt.isDefault);

      if (defaultOption) {
        initialOptionId = defaultOption.id;
      } else if (menuItem.optionsAreRequired && options.length === 1 && options[0]) {
        // Auto-select the only option when required
        initialOptionId = options[0].id;
      }

      setSelectedItems([
        ...selectedItems,
        {
          menuItem,
          quantity: 1,
          notes: '',
          selectedOptionId: initialOptionId,
          options: options.length > 0 ? options : [],
        } as OrderItem,
      ]);
    }
  };

  const updateSelectedOption = (menuItemId: string, optionId: string) => {
    setSelectedItems(
      selectedItems.map((item) =>
        item.menuItem.id === menuItemId
          ? { ...item, selectedOptionId: optionId === 'none' ? null : optionId }
          : item
      )
    );
  };

  const updateQuantity = (menuItemId: string, delta: number) => {
    const menuItem = menuItems.find((item) => item.id === menuItemId);
    if (!menuItem) return;

    const availability = availabilityMap[menuItemId];
    const availableQuantity = allowNegativeStock ? Infinity : (availability?.availableQuantity ?? 999999);

    setSelectedItems(
      selectedItems.map((item) => {
        if (item.menuItem.id === menuItemId) {
          const newQuantity = item.quantity + delta;
          // Don't allow quantity below 1 or above available stock (unless negative stock allowed)
          const clampedQuantity = allowNegativeStock
            ? Math.max(1, newQuantity)
            : Math.max(1, Math.min(newQuantity, availableQuantity));

          if (!allowNegativeStock && clampedQuantity !== newQuantity && newQuantity > availableQuantity) {
            toast({
              title: t('order.insufficientStock'),
              description: t('order.onlyXAvailable', { count: availability?.availableQuantity ?? 0 }),
              variant: 'destructive',
            });
          }

          return { ...item, quantity: clampedQuantity };
        }
        return item;
      })
    );
  };

  const updateNotes = (menuItemId: string, notes: string) => {
    setSelectedItems(
      selectedItems.map((item) => (item.menuItem.id === menuItemId ? { ...item, notes } : item))
    );
  };

  const removeItem = (menuItemId: string) => {
    setSelectedItems(selectedItems.filter((item) => item.menuItem.id !== menuItemId));
  };

  // Calculate totals (including option price modifiers)
  const newItemsTotal = selectedItems.reduce((sum, item) => {
    const selectedOption = item.options?.find((opt) => opt.id === item.selectedOptionId);
    const priceModifier = selectedOption?.priceModifier || 0;
    return sum + (item.menuItem.price + priceModifier) * item.quantity;
  }, 0);

  const existingItemsTotal = existingSubOrders.reduce(
    (total, subOrder) =>
      total +
      subOrder.items.reduce(
        (sum, item) => sum + (item.priceAtOrder || item.menuItem?.price || 0) * item.quantity,
        0
      ),
    0
  );

  const hasExistingSubOrders = existingSubOrders.length > 0;

  const totalAmount = newItemsTotal + existingItemsTotal;

  const handleSubmit = async () => {
    if (selectedItems.length === 0) {
      toast({
        title: t('error.generic'),
        description: t('order.selectAtLeastOneItem'),
        variant: 'destructive',
      });
      return;
    }

    // Validate that items with optionsAreRequired have an option selected
    const itemMissingRequiredOption = selectedItems.find(
      (item) =>
        item.menuItem.optionsAreRequired &&
        item.options &&
        item.options.length > 0 &&
        !item.selectedOptionId
    );

    if (itemMissingRequiredOption) {
      const itemName =
        i18n.language === 'ar'
          ? itemMissingRequiredOption.menuItem.nameAr
          : itemMissingRequiredOption.menuItem.nameFr;

      // On mobile, switch to review step first
      if (isMobile && mobileStep === 'menu') {
        setMobileStep('review');
      }

      // Scroll to the item with missing option
      setTimeout(() => {
        const element = document.getElementById(`order-item-${itemMissingRequiredOption.menuItem.id}`);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
          // Add a brief highlight effect
          element.classList.add('ring-2', 'ring-destructive');
          setTimeout(() => {
            element.classList.remove('ring-2', 'ring-destructive');
          }, 2000);
        }
      }, 100);

      toast({
        title: t('menu.optionRequired'),
        description: itemName,
        variant: 'destructive',
      });
      return;
    }

    try {
      await createOrder.mutateAsync({
        tableId,
        staffId,
        items: selectedItems.map((item) => {
          const selectedOption = item.options?.find((opt) => opt.id === item.selectedOptionId);
          const priceModifier = selectedOption?.priceModifier || 0;
          return {
            menuItemId: item.menuItem.id,
            quantity: item.quantity,
            price: item.menuItem.price + priceModifier,
            ...(item.notes ? { notes: item.notes } : {}),
            ...(item.selectedOptionId ? { menuItemOptionId: item.selectedOptionId } : {}),
          };
        }),
      });

      toast({
        title: t('common.success'),
        description: hasExistingSubOrders ? t('order.itemsAdded') : t('order.orderCreated'),
      });

      setSelectedItems([]);
      setSearchQuery('');
      setExistingSubOrders([]);
      onOpenChange(false);
    } catch (error) {
      console.error('Failed to create order:', error);
      toast({
        title: t('common.error'),
        description: t('error.generic'),
        variant: 'destructive',
      });
    }
  };

  const handleDeleteOrderItem = async (
    orderItemId: string,
    subOrderId: string,
    subOrderStatus: string
  ) => {
    // Only allow deletion from pending orders
    if (subOrderStatus !== 'pending') {
      toast({
        title: t('common.error'),
        description: t('order.cannotEditNonPendingOrder'),
        variant: 'destructive',
      });
      return;
    }

    try {
      await deleteOrderItem.mutateAsync({
        orderItemId,
        staffId,
        orderId: subOrderId,
        tableId,
      });

      toast({
        title: t('common.success'),
        description: t('order.itemDeleted'),
      });
    } catch (error: any) {
      console.error('Failed to delete order item:', error);
      toast({
        title: t('common.error'),
        description: error.message || t('error.generic'),
        variant: 'destructive',
      });
    }
  };

  // Check if we're on mobile (viewport width < 768px)
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[95vw] sm:w-[90vw] md:min-w-[70vw] md:max-w-[90vw] max-h-[95vh] overflow-hidden flex flex-col p-4 sm:p-6">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className="text-lg sm:text-xl">
            {hasExistingSubOrders ? t('order.addItemsToOrder') : t('order.createOrder')} -{' '}
            {t('table.table')} #{tableNumber}
            {isMobile && mobileStep === 'review' && (
              <span className="text-sm font-normal text-muted-foreground ml-2">
                ({t('order.step')} 2/2)
              </span>
            )}
            {isMobile && mobileStep === 'menu' && (
              <span className="text-sm font-normal text-muted-foreground ml-2">
                ({t('order.step')} 1/2)
              </span>
            )}
          </DialogTitle>
          <DialogDescription className="text-sm">
            {isMobile && mobileStep === 'menu' && t('order.selectMenuItems')}
            {isMobile && mobileStep === 'review' && t('order.reviewAndConfirm')}
            {!isMobile && (hasExistingSubOrders
              ? t('order.addMoreItemsToExistingOrder')
              : t('order.selectMenuItems'))}
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col md:grid md:grid-cols-2 gap-4 md:gap-6 flex-1 min-h-0 overflow-y-auto">
          {/* Menu Items - Show on desktop always, or on mobile when step is 'menu' */}
          <div className={`space-y-3 flex flex-col min-h-0 flex-shrink-0 ${isMobile && mobileStep === 'review' ? 'hidden' : ''}`}>
            <div className="flex-shrink-0">
              <Label className="text-sm sm:text-base">{t('menu.title')}</Label>
              <Input
                placeholder={t('common.search')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="mt-1 text-sm sm:text-base"
              />
            </div>

            <ScrollArea className="flex-1 border rounded-md p-2 sm:p-3 min-h-[40vh] md:min-h-0">
              {isLoading || isLoadingAvailability ? (
                <div className="text-center text-sm text-muted-foreground py-8">
                  {t('common.loading')}
                </div>
              ) : filteredMenuItems.length === 0 ? (
                <div className="text-center text-sm text-muted-foreground py-8">
                  {t('menu.noMenuItems')}
                </div>
              ) : (
                <div className="space-y-4">
                  {Object.entries(groupedMenuItems).map(([categoryId, items]) => {
                    const isExpanded = expandedCategories.has(categoryId);
                    return (
                      <div key={categoryId} className="space-y-2">
                        <button
                          type="button"
                          onClick={() => toggleCategory(categoryId)}
                          className="w-full flex items-center gap-2 font-semibold text-xs sm:text-sm text-muted-foreground uppercase tracking-wide hover:text-foreground transition-colors"
                        >
                          {isExpanded ? (
                            <ChevronDown className="w-4 h-4" />
                          ) : (
                            <ChevronRight className="w-4 h-4" />
                          )}
                          <span>
                            {getCategoryName(categoryId === 'uncategorized' ? null : categoryId)}
                          </span>
                          <span className="text-xs normal-case">({items.length})</span>
                        </button>
                        {isExpanded && (
                          <div className="space-y-2 ms-6">
                            {items.map((item) => (
                              <div
                                key={item.id}
                                className={`flex items-center justify-between p-3 sm:p-4 border rounded-lg transition-colors ${
                                  !allowNegativeStock && availabilityMap[item.id]?.availableQuantity === 0
                                    ? 'opacity-50 cursor-not-allowed bg-muted'
                                    : 'hover:bg-accent cursor-pointer'
                                }`}
                                onClick={() => {
                                  if (allowNegativeStock || availabilityMap[item.id]?.availableQuantity !== 0) {
                                    addItem(item);
                                  }
                                }}
                              >
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2 sm:gap-3">
                                    {item.imageUrl && (
                                      <img
                                        src={item.imageUrl}
                                        alt={i18n.language === 'ar' ? item.nameAr : item.nameFr}
                                        className="w-12 h-12 sm:w-16 sm:h-16 object-cover rounded flex-shrink-0"
                                      />
                                    )}
                                    <div className="flex-1 min-w-0">
                                      <p className="font-semibold text-sm sm:text-base mb-1">
                                        {i18n.language === 'ar' ? item.nameAr : item.nameFr}
                                      </p>
                                      {item.descriptionAr && item.descriptionFr && (
                                        <p className="text-xs sm:text-sm text-muted-foreground line-clamp-2">
                                          {i18n.language === 'ar'
                                            ? item.descriptionAr
                                            : item.descriptionFr}
                                        </p>
                                      )}
                                      <MenuItemSpecialtiesBadges specialties={allSpecialtiesMap?.get(item.id) || []} />
                                      {(() => {
                                        const availability = availabilityMap[item.id];
                                        if (
                                          availability &&
                                          availability.availableQuantity < 999999
                                        ) {
                                          return (
                                            <>
                                              <p className="text-xs text-orange-600 dark:text-orange-400 mt-1 font-medium">
                                                {availability.availableQuantity === 0
                                                  ? t('order.outOfStock')
                                                  : t('order.onlyXLeft', {
                                                      count: availability.availableQuantity,
                                                    })}
                                              </p>
                                              {availability.availableQuantity === 0 &&
                                                availability.missingIngredients &&
                                                availability.missingIngredients.length > 0 && (
                                                  <div className="flex flex-wrap gap-1 mt-1">
                                                    {availability.missingIngredients.map((ing) => (
                                                      <span
                                                        key={ing.ingredientId}
                                                        className="inline-flex items-center gap-1 text-xs text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-950 px-1.5 py-0.5 rounded"
                                                      >
                                                        <IngredientIcon
                                                          ingredient={{
                                                            nameAr: ing.nameAr,
                                                            nameFr: ing.nameFr,
                                                            emoji: ing.emoji,
                                                          }}
                                                          size={16}
                                                        />
                                                        <span>
                                                          {i18n.language === 'ar'
                                                            ? ing.nameAr
                                                            : ing.nameFr}
                                                        </span>
                                                      </span>
                                                    ))}
                                                  </div>
                                                )}
                                            </>
                                          );
                                        }
                                        return null;
                                      })()}
                                    </div>
                                  </div>
                                </div>
                                <div className="flex flex-col items-end gap-1 flex-shrink-0">
                                  <Badge
                                    variant="secondary"
                                    className="text-xs sm:text-base px-2 sm:px-3 py-1 sm:py-1.5"
                                  >
                                    {item.price.toFixed(2)} DH
                                  </Badge>
                                  {(() => {
                                    const availability = availabilityMap[item.id];
                                    if (!allowNegativeStock && availability && availability.availableQuantity === 0) {
                                      return (
                                        <Badge
                                          variant="destructive"
                                          className="text-xs px-2 py-0.5"
                                        >
                                          {t('order.unavailable')}
                                        </Badge>
                                      );
                                    }
                                    return null;
                                  })()}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </ScrollArea>
          </div>

          {/* Selected Items - Show on desktop always, or on mobile when step is 'review' */}
          <div className={`space-y-3 flex flex-col min-h-0 flex-shrink-0 ${isMobile && mobileStep === 'menu' ? 'hidden' : ''}`}>
            <div className="flex items-center justify-between flex-shrink-0">
              <Label className="text-sm sm:text-base">
                {hasExistingSubOrders
                  ? t('order.addingItems') + ` (${selectedItems.length})`
                  : t('order.selectedItems') + ` (${selectedItems.length})`}
              </Label>
              {(selectedItems.length > 0 || hasExistingSubOrders) && (
                <div className="flex items-center gap-2 text-xs sm:text-sm font-semibold">
                  {hasExistingSubOrders && existingItemsTotal > 0 && (
                    <span className="text-muted-foreground">
                      {t('order.existingTotal')}: {existingItemsTotal.toFixed(2)} DH
                    </span>
                  )}
                  {selectedItems.length > 0 && (
                    <span className="text-muted-foreground">
                      {t('order.newItemsTotal')}: {newItemsTotal.toFixed(2)} DH
                    </span>
                  )}
                  <span className="text-primary font-bold">
                    {t('order.total')}: {totalAmount.toFixed(2)} DH
                  </span>
                </div>
              )}
            </div>

            <ScrollArea className="flex-1 border rounded-md p-2 sm:p-3 min-h-[40vh] md:min-h-0">
              {/* Existing Sub-Orders Items */}
              {hasExistingSubOrders && (
                <div className="mb-4 pb-4 border-b">
                  <div className="text-xs font-semibold text-muted-foreground mb-2 uppercase">
                    {t('order.existingItems')}
                  </div>
                  <div className="space-y-3">
                    {existingSubOrders.map((subOrder) => {
                      const subOrderTotal = subOrder.items.reduce((sum, item) => {
                        const price = item.priceAtOrder || item.menuItem?.price || 0;
                        return sum + price * item.quantity;
                      }, 0);

                      return (
                        <div key={subOrder.id} className="space-y-2">
                          <div className="flex items-center justify-between text-xs font-medium">
                            <span className="text-muted-foreground">
                              {t('order.subOrder')} #{subOrder.id.slice(0, 8)}
                            </span>
                            <Badge
                              className={
                                subOrder.status === 'ready'
                                  ? 'bg-green-500'
                                  : subOrder.status === 'in_progress'
                                    ? 'bg-blue-500'
                                    : 'bg-yellow-500'
                              }
                            >
                              {t(`order.statuses.${subOrder.status}`)}
                            </Badge>
                          </div>
                          <div className="space-y-1 ms-2">
                            {subOrder.items.map((item) => (
                              <div
                                key={item.id}
                                className="p-2 bg-muted rounded text-xs sm:text-sm"
                              >
                                <div className="flex items-center justify-between gap-2">
                                  <div className="flex-1 min-w-0">
                                    <p className="font-medium text-xs sm:text-sm">
                                      {i18n.language === 'ar'
                                        ? item.menuItem?.nameAr
                                        : item.menuItem?.nameFr}
                                    </p>
                                    <p className="text-xs text-muted-foreground">
                                      {(item.priceAtOrder || item.menuItem?.price || 0).toFixed(2)}{' '}
                                      DH × {item.quantity} ={' '}
                                      {(
                                        (item.priceAtOrder || item.menuItem?.price || 0) *
                                        item.quantity
                                      ).toFixed(2)}{' '}
                                      DH
                                    </p>
                                    {item.notes && (
                                      <p className="text-xs text-muted-foreground italic mt-1">
                                        {t('order.notes')}: {item.notes}
                                      </p>
                                    )}
                                  </div>
                                  {/* Only show delete button for pending orders */}
                                  {subOrder.status === 'pending' && (
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      className="h-6 w-6 sm:h-7 sm:w-7 text-destructive hover:text-destructive hover:bg-destructive/10 flex-shrink-0"
                                      onClick={() =>
                                        handleDeleteOrderItem(item.id, subOrder.id, subOrder.status)
                                      }
                                      disabled={deleteOrderItem.isPending}
                                    >
                                      <Trash2 className="h-3 w-3 sm:h-4 sm:w-4" />
                                    </Button>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                          <div className="text-xs font-medium text-right text-muted-foreground ms-2">
                            {t('order.subOrderTotal')}: {subOrderTotal.toFixed(2)} DH
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* New Items Being Added */}
              {selectedItems.length === 0 && !hasExistingSubOrders ? (
                <div className="text-center text-sm text-muted-foreground py-8">
                  {t('order.noItemsSelected')}
                </div>
              ) : selectedItems.length === 0 && hasExistingSubOrders ? (
                <div className="text-center text-sm text-muted-foreground py-8">
                  {t('order.selectItemsToAdd')}
                </div>
              ) : (
                <div className="space-y-3">
                  {hasExistingSubOrders && (
                    <div className="text-xs font-semibold text-muted-foreground mb-2 uppercase">
                      {t('order.newItems')}
                    </div>
                  )}
                  {selectedItems.map((item) => {
                    const selectedOption = item.options?.find(
                      (opt) => opt.id === item.selectedOptionId
                    );
                    const priceModifier = selectedOption?.priceModifier || 0;
                    const itemPrice = item.menuItem.price + priceModifier;

                    return (
                      <div
                        key={item.menuItem.id}
                        id={`order-item-${item.menuItem.id}`}
                        className="p-2 sm:p-3 border rounded-lg space-y-2 transition-all"
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-xs sm:text-sm">
                              {i18n.language === 'ar' ? item.menuItem.nameAr : item.menuItem.nameFr}
                            </p>
                            <MenuItemSpecialtiesBadges specialties={allSpecialtiesMap?.get(item.menuItem.id) || []} />
                            <p className="text-xs text-muted-foreground mt-1">
                              {itemPrice.toFixed(2)} DH × {item.quantity} ={' '}
                              {(itemPrice * item.quantity).toFixed(2)} DH
                            </p>
                          </div>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => removeItem(item.menuItem.id)}
                            className="h-7 w-7 sm:h-8 sm:w-8 flex-shrink-0"
                          >
                            <Trash2 className="h-3 w-3 sm:h-4 sm:w-4" />
                          </Button>
                        </div>

                        {/* Option Selector */}
                        {item.options && item.options.length > 0 && (
                          <div className="space-y-1">
                            <div className="flex items-center gap-2">
                              <Label className="text-xs">{t('menu.selectOption')}</Label>
                              {item.menuItem.optionsAreRequired && (
                                <Badge variant="destructive" className="text-[10px] px-1.5 py-0">
                                  {t('menu.optionsAreRequired')}
                                </Badge>
                              )}
                            </div>
                            <Select
                              value={item.selectedOptionId || 'none'}
                              onValueChange={(value) =>
                                updateSelectedOption(item.menuItem.id, value)
                              }
                            >
                              <SelectTrigger
                                className={`h-8 sm:h-9 text-xs sm:text-sm ${
                                  item.menuItem.optionsAreRequired && !item.selectedOptionId
                                    ? 'border-destructive ring-destructive'
                                    : ''
                                }`}
                              >
                                <SelectValue placeholder={t('menu.selectOption')} />
                              </SelectTrigger>
                              <SelectContent>
                                {/* Empty option - only show if options are NOT required */}
                                {!item.menuItem.optionsAreRequired && (
                                  <SelectItem value="none">
                                    <span className="text-xs sm:text-sm text-muted-foreground">
                                      {t('menu.noOption')}
                                    </span>
                                  </SelectItem>
                                )}
                                {item.options.map((option) => (
                                  <SelectItem key={option.id} value={option.id}>
                                    <div className="flex items-center justify-between gap-2">
                                      <span className="text-xs sm:text-sm">
                                        {i18n.language === 'ar' ? option.nameAr : option.nameFr}
                                      </span>
                                      {option.priceModifier !== 0 && (
                                        <span className="text-xs text-muted-foreground">
                                          {option.priceModifier > 0 ? '+' : ''}
                                          {formatPrice(option.priceModifier)} DH
                                        </span>
                                      )}
                                    </div>
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            {item.menuItem.optionsAreRequired && !item.selectedOptionId && (
                              <p className="text-xs text-destructive">
                                {t('menu.optionRequired')}
                              </p>
                            )}
                          </div>
                        )}

                        <div className="flex items-center gap-2">
                          <Button
                            variant="outline"
                            size="icon"
                            onClick={() => updateQuantity(item.menuItem.id, -1)}
                            disabled={item.quantity <= 1}
                            className="h-7 w-7 sm:h-8 sm:w-8"
                          >
                            <Minus className="h-3 w-3" />
                          </Button>
                          <span className="text-xs sm:text-sm font-medium w-6 sm:w-8 text-center">
                            {item.quantity}
                          </span>
                          {(() => {
                            const availability = availabilityMap[item.menuItem.id];
                            const availableQuantity = allowNegativeStock ? Infinity : (availability?.availableQuantity ?? 999999);
                            return (
                              <Button
                                variant="outline"
                                size="icon"
                                onClick={() => updateQuantity(item.menuItem.id, 1)}
                                disabled={!allowNegativeStock && item.quantity >= availableQuantity}
                                className="h-7 w-7 sm:h-8 sm:w-8"
                              >
                                <Plus className="h-3 w-3" />
                              </Button>
                            );
                          })()}
                        </div>
                        {(() => {
                          const availability = availabilityMap[item.menuItem.id];
                          if (availability && availability.availableQuantity < 999999) {
                            return (
                              <p className="text-xs text-orange-600 dark:text-orange-400">
                                {availability.availableQuantity === 0
                                  ? t('order.outOfStock')
                                  : t('order.onlyXLeft', { count: availability.availableQuantity })}
                              </p>
                            );
                          }
                          return null;
                        })()}

                        {/* Quick Notes Buttons */}
                        {(() => {
                          const categoryId = item.menuItem.categoryId;
                          const category = categories.find(c => c.id === categoryId);
                          const quickNotes = category?.quickNotes || [];
                          
                          if (quickNotes.length > 0) {
                            return (
                              <div className="flex flex-wrap gap-1.5 mb-1.5">
                                {quickNotes.map((note, noteIndex) => (
                                  <Button
                                    key={noteIndex}
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    className="h-6 px-2 text-xs"
                                    onClick={() => {
                                      const currentNotes = item.notes;
                                      const newNotes = currentNotes 
                                        ? `${currentNotes}, ${note}` 
                                        : note;
                                      updateNotes(item.menuItem.id, newNotes);
                                    }}
                                  >
                                    {note}
                                  </Button>
                                ))}
                              </div>
                            );
                          }
                          return null;
                        })()}
                        
                        <Textarea
                          placeholder={t('order.notes')}
                          value={item.notes}
                          onChange={(e) => updateNotes(item.menuItem.id, e.target.value)}
                          className="text-xs sm:text-sm h-8 sm:h-9 min-h-[32px] resize-none"
                          rows={1}
                        />
                      </div>
                    );
                  })}
                </div>
              )}
            </ScrollArea>
          </div>
        </div>

        {/* Desktop Footer or Mobile Step 2 Footer */}
        {(!isMobile || mobileStep === 'review') && (
          <DialogFooter className="flex-shrink-0 flex-row gap-2 pt-4 mt-4 border-t bg-background">
            <Button
              variant="outline"
              onClick={() => {
                if (isMobile && mobileStep === 'review') {
                  setMobileStep('menu');
                } else {
                  onOpenChange(false);
                }
              }}
              className="flex-1 sm:flex-none sm:w-auto"
            >
              {isMobile && mobileStep === 'review' ? t('common.back') : t('common.cancel')}
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={selectedItems.length === 0 || createOrder.isPending}
              className="flex-1 sm:flex-none sm:w-auto"
            >
              {createOrder.isPending
                ? t('common.loading')
                : hasExistingSubOrders
                  ? t('order.addItems')
                  : t('order.submitOrder')}
            </Button>
          </DialogFooter>
        )}

        {/* Mobile Floating Button on Step 1 */}
        {isMobile && mobileStep === 'menu' && (
          <div className="fixed bottom-20 right-4 z-50 flex flex-col gap-2">
            {selectedItems.length > 0 && (
              <Button
                onClick={() => setMobileStep('review')}
                className="shadow-lg h-14 px-6 rounded-full"
                size="lg"
              >
                <div className="flex flex-col items-center">
                  <span className="text-sm font-semibold">
                    {t('order.nextPreview')} ({selectedItems.length})
                  </span>
                  <span className="text-xs">
                    {newItemsTotal.toFixed(2)} DH
                  </span>
                </div>
              </Button>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
